/* IdenScan homepage — one motion/interaction system.
   - Section reveals (IntersectionObserver), respects prefers-reduced-motion.
   - Hero staged load.
   - Audience tab switch (functional; works even under reduced motion).
   - Product-story active-step highlight on scroll.
   Progressive enhancement: without JS, content stays fully visible and readable. */
(function () {
  'use strict';

  var docEl = document.documentElement;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasIO = 'IntersectionObserver' in window;

  function initTabs() {
    var groups = document.querySelectorAll('[data-ids-tabs]');
    groups.forEach(function (group) {
      var tabs = Array.prototype.slice.call(group.querySelectorAll('[data-ids-tab]'));
      var panels = Array.prototype.slice.call(group.querySelectorAll('[data-ids-panel]'));
      var stage = group.querySelector('.ids-h-stage');
      if (!tabs.length || !panels.length) return;
      if (stage) stage.classList.add('is-enhanced');

      function setInactive(panel, inactive) {
        if (inactive) { panel.setAttribute('aria-hidden', 'true'); if ('inert' in panel) panel.inert = true; }
        else { panel.removeAttribute('aria-hidden'); if ('inert' in panel) panel.inert = false; }
      }

      function render(key, focusTab) {
        tabs.forEach(function (tab) {
          var active = tab.getAttribute('data-ids-tab') === key;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.setAttribute('tabindex', active ? '0' : '-1');
          if (active && focusTab) tab.focus();
        });
        panels.forEach(function (panel) {
          var active = panel.getAttribute('data-ids-panel') === key;
          panel.classList.toggle('is-active', active);
          setInactive(panel, !active);
        });
      }

      // Initial state from the tab marked active in markup (personal default).
      var initial = (group.querySelector('[data-ids-tab].is-active') || tabs[0]).getAttribute('data-ids-tab');
      panels.forEach(function (p) { setInactive(p, p.getAttribute('data-ids-panel') !== initial); });

      tabs.forEach(function (tab, index) {
        tab.addEventListener('click', function () { render(tab.getAttribute('data-ids-tab'), false); });
        tab.addEventListener('keydown', function (e) {
          var n = index;
          if (e.key === 'ArrowRight') n = (index + 1) % tabs.length;
          else if (e.key === 'ArrowLeft') n = (index - 1 + tabs.length) % tabs.length;
          else if (e.key === 'Home') n = 0;
          else if (e.key === 'End') n = tabs.length - 1;
          else return;
          e.preventDefault();
          render(tabs[n].getAttribute('data-ids-tab'), true);
        });
      });

      function followHash() {
        if (window.location.hash === '#voor-bedrijven') render('business', false);
        else if (window.location.hash === '#voor-persoonlijk') render('personal', false);
      }
      window.addEventListener('hashchange', followHash);
      followHash();
    });
  }

  function initReveals() {
    if (reduceMotion || !hasIO) return;
    docEl.classList.add('ids-reveal-ready');

    var targets = document.querySelectorAll('[data-ids-reveal], [data-ids-reveal-group], [data-ids-reveal-media]');
    if (targets.length) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
      targets.forEach(function (el) { io.observe(el); });
    }

    // Hero staged load
    var hero = document.querySelector('.ids-h-hero');
    if (hero) { window.requestAnimationFrame(function () { window.requestAnimationFrame(function () { hero.classList.add('is-loaded'); }); }); }

    // Safety net after load
    window.addEventListener('load', function () {
      window.setTimeout(function () {
        targets.forEach(function (el) {
          var r = el.getBoundingClientRect();
          if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('is-in');
        });
      }, 200);
    });
  }

  function initStory() {
    if (!hasIO) return;
    var steps = Array.prototype.slice.call(document.querySelectorAll('[data-ids-step]'));
    if (steps.length < 2) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          steps.forEach(function (s) { s.classList.toggle('is-active', s === e.target); });
        }
      });
    }, { rootMargin: '-45% 0px -45% 0px', threshold: 0 });
    steps.forEach(function (s) { io.observe(s); });
  }

  function initGallery() {
    document.querySelectorAll('[data-ids-gallery]').forEach(function (gallery) {
      var viewport = gallery.querySelector('[data-ids-gallery-viewport]');
      var slides = Array.prototype.slice.call(gallery.querySelectorAll('[data-ids-gallery-slide]'));
      var dots = Array.prototype.slice.call(gallery.querySelectorAll('[data-ids-gallery-dot]'));
      var previous = gallery.querySelector('[data-ids-gallery-prev]');
      var next = gallery.querySelector('[data-ids-gallery-next]');
      var status = gallery.querySelector('[data-ids-gallery-status]');
      if (!viewport || !slides.length || !previous || !next || !status) return;
      var current = 0;
      var visible = 1;
      var maxIndex = slides.length - 1;
      var scrollTimer;
      function measure() {
        var gap = parseFloat(window.getComputedStyle(gallery.querySelector('.ids-h-gallery__track')).gap) || 0;
        visible = Math.max(1, Math.min(slides.length, Math.floor((viewport.clientWidth + gap + 2) / (slides[0].getBoundingClientRect().width + gap))));
        maxIndex = slides.length - visible;
        current = Math.min(current, maxIndex);
        update();
      }
      function update() {
        previous.disabled = current === 0;
        next.disabled = current === maxIndex;
        var end = Math.min(current + visible, slides.length);
        status.textContent = visible === 1 ? 'Afbeelding ' + (current + 1) + ' van ' + slides.length : 'Afbeeldingen ' + (current + 1) + '–' + end + ' van ' + slides.length;
        dots.forEach(function (dot, index) {
          dot.hidden = index > maxIndex;
          dot.setAttribute('aria-current', String(index === current));
          dot.setAttribute('aria-label', 'Ga naar afbeeldingen ' + (index + 1) + '–' + Math.min(index + visible, slides.length));
        });
      }
      function go(index) {
        current = Math.max(0, Math.min(maxIndex, index));
        viewport.scrollTo({ left: slides[current].offsetLeft - slides[0].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
        update();
      }
      previous.addEventListener('click', function () { go(current - 1); });
      next.addEventListener('click', function () { go(current + 1); });
      dots.forEach(function (dot, index) { dot.addEventListener('click', function () { go(index); }); });
      viewport.addEventListener('scroll', function () {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(function () {
          var distance = slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : 1;
          current = Math.max(0, Math.min(maxIndex, Math.round(viewport.scrollLeft / distance)));
          update();
        }, 100);
      }, { passive: true });
      window.addEventListener('resize', measure);
      measure();
    });
  }

  function init() {
    initTabs();
    initReveals();
    initStory();
    initGallery();
    document.querySelectorAll('[data-ids-training-showcase]').forEach(function (showcase) {
      var tabs = Array.prototype.slice.call(showcase.querySelectorAll('[data-ids-scenario-tab]'));
      var scenes = Array.prototype.slice.call(showcase.querySelectorAll('[data-ids-scenario]'));
      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          tabs.forEach(function (item) { item.setAttribute('aria-pressed', String(item === tab)); });
          scenes.forEach(function (scene) { scene.hidden = scene.dataset.idsScenario !== tab.dataset.idsScenarioTab; });
        });
      });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
